import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'

/**
 * One Settings section's unsaved edits. `save` throws when the API refuses; `blocked` says why the
 * edits cannot be saved yet, which holds the whole page's Save.
 */
export type SettingsSection = {
  dirty: boolean
  blocked?: string
  save: () => Promise<void>
  discard: () => void
}

type SectionFlags = { dirty: boolean; blocked?: string }
type Registry = { report: (id: string, section: SettingsSection | null) => void }

export const SettingsSaveContext = createContext<Registry | null>(null)

/**
 * The page side of inline editing on Settings: every section reports its edits here, and one save
 * bar saves them all in turn, then reloads the agent once.
 */
export function useSettingsSave(onSaved: () => void) {
  const handlers = useRef(new Map<string, SettingsSection>())
  const [flags, setFlags] = useState<Record<string, SectionFlags>>({})
  const [saving, setSaving] = useState(false)

  const registry = useMemo<Registry>(
    () => ({
      report: (id, section) => {
        if (section) handlers.current.set(id, section)
        else handlers.current.delete(id)
        setFlags((current) => {
          const before = current[id]
          if (!section) {
            if (!before) return current
            const { [id]: _, ...rest } = current
            return rest
          }
          if (before?.dirty === section.dirty && before?.blocked === section.blocked) return current
          return { ...current, [id]: { dirty: section.dirty, blocked: section.blocked } }
        })
      },
    }),
    [],
  )

  const dirtyIds = Object.keys(flags).filter((id) => flags[id].dirty)
  const blocked = dirtyIds.map((id) => flags[id].blocked).find(Boolean)

  const saveAll = async () => {
    const pending = dirtyIds
      .map((id) => handlers.current.get(id))
      .filter((section): section is SettingsSection => section != null)
    setSaving(true)
    try {
      for (const section of pending) await section.save()
      pending.forEach((section) => section.discard())
      notifySuccess(`Your agent is updated`)
    } catch (error) {
      logError(`Agent Settings: could not save`, error)
      notifyError(`Some changes could not be saved. Try again in a moment.`)
    } finally {
      setSaving(false)
      onSaved()
    }
  }

  const discardAll = () => dirtyIds.forEach((id) => handlers.current.get(id)?.discard())

  return { registry, dirty: dirtyIds.length > 0, blocked, saving, saveAll, discardAll }
}

/** A section's side: report its edits on every render so the save bar always calls current code. */
export function useSettingsSection(id: string, section: SettingsSection): void {
  const registry = useContext(SettingsSaveContext)
  useEffect(() => {
    registry?.report(id, section)
  })
  useEffect(() => () => registry?.report(id, null), [registry, id])
}
